import React, {
  useCallback,
  useEffect,
  Fragment,
  useState,
  useMemo,
} from 'react';
import * as PropTypes from 'prop-types';
import cn from 'classnames';

import Icon from '../../../Icon';
import Button from '../../../Button';
import Loader from '../../../common/Loader';
import Checkbox from '../../../common/Checkbox';
import BackButton from '../../../common/BackButton';
import RefillCard from '../DataList/Card/RefillCard';
import PreferredPharmacy from './PreferredPharmacy';
import { toggleElementInList } from '../../../../utils/arrayModify';
import { enhance } from './enhance';

import styles from './index.styl';

function Refills({
  clear,
  onClose,
  safeUser,
  pendingHistory,
  definePharmacy,
  clearRefillRequest,
  refillRequestError,
  refillsHistoryList,
  setPreferredPharmacy,
  refillRequestPending,
  initMedicationRefill,
  getAvailableMedications,
  refillsAvailableMedications,
  refillsAvailableMedicationsPending,
  options: { viewHistory, formatMessage },
}) {
  const [medications, setMedications] = useState([]);

  useEffect(() => {
    if (!viewHistory) {
      getAvailableMedications({ userId: safeUser.id });
    }
  }, [viewHistory, safeUser]);

  useEffect(
    () => () => {
      clear();
      clearRefillRequest();
    },
    [],
  );

  const handleCheckMedications = useCallback(
    id => {
      setMedications(toggleElementInList(medications, id));
    },
    [medications],
  );

  const emptyState = useMemo(
    () => (
      <div className={styles.emptyStateRoot}>
        <Icon size={60} name="search-list" />
        <div className={styles.noDataMessage}>
          {formatMessage({ id: 'patient.portal.requestRefillProvider' })}
        </div>
      </div>
    ),
    [],
  );

  const getHistoryView = useMemo(
    () => (
      <Fragment>
        {pendingHistory && <Loader />}
        <div className={styles.refillList}>
          {!pendingHistory &&
            refillsHistoryList.length > 0 &&
            refillsHistoryList.map(item => (
              <RefillCard entity={item} key={item.id} />
            ))}
        </div>
      </Fragment>
    ),
    [pendingHistory, refillsHistoryList],
  );

  const handleStartRequest = useCallback(() => {
    initMedicationRefill({ medicationIds: medications, userId: safeUser.id });
  }, [medications, safeUser]);

  const getRequestRefillView = useMemo(
    () => (
      <Fragment>
        {refillsAvailableMedicationsPending ? (
          <Loader />
        ) : (
          refillsAvailableMedications.length > 0 && (
            <div
              className={cn(styles.requestContainer, {
                [styles.opacity]: refillRequestPending,
              })}
            >
              {refillRequestPending && (
                <Loader className={styles.requestLoader} />
              )}
              <PreferredPharmacy
                user={safeUser}
                formatMessage={formatMessage}
                clearRefillRequest={clearRefillRequest}
                refillRequestError={refillRequestError}
                refillRequestPending={refillRequestPending}
                setPreferredPharmacy={setPreferredPharmacy}
              />
              <div className={styles.medicationContainer}>
                <div className={styles.title}>
                  {formatMessage({ id: 'patient.portal.selectMedications' })}
                </div>
                <div className={styles.list}>
                  {refillsAvailableMedications.map(
                    ({ id, quantityAmount, quantityUnit, name }) => (
                      <div className={styles.medicationWrapper} key={id}>
                        <Checkbox
                          id={id}
                          size={16}
                          checked={medications.includes(id)}
                          onChange={() => handleCheckMedications(id)}
                        />
                        <div className={styles.medication}>
                          <div className={styles.medicationName}>
                            {`${name}${
                              quantityAmount
                                ? `, ${quantityAmount}${quantityUnit || ''}`
                                : ''
                            }`}
                          </div>
                        </div>
                      </div>
                    ),
                  )}
                </div>
                <Button
                  raised
                  color="primary"
                  onClick={handleStartRequest}
                  className={styles.confirmButton}
                  disabled={
                    !medications.length ||
                    !definePharmacy ||
                    refillRequestPending
                  }
                >
                  {formatMessage({ id: 'submit.fillProfile' })}
                </Button>
              </div>
            </div>
          )
        )}
        {!refillsAvailableMedicationsPending &&
          refillsAvailableMedications.length === 0 &&
          emptyState}
      </Fragment>
    ),
    [
      safeUser,
      medications,
      definePharmacy,
      refillRequestError,
      refillRequestPending,
      refillsAvailableMedications,
      refillsAvailableMedicationsPending,
    ],
  );

  return (
    <div className={styles.root}>
      <div className={styles.maskHeader} onClick={onClose} />
      <BackButton onBack={onClose} className={styles.backBtn} minWidth={100} />
      <div className={styles.header}>
        <p className={styles.headerTitle}>
          {formatMessage({
            id: viewHistory
              ? 'patient.portal.refillHistory'
              : 'patient.portal.requestRefills',
          })}
        </p>
      </div>
      {viewHistory ? getHistoryView : getRequestRefillView}
    </div>
  );
}

Refills.propTypes = {
  clear: PropTypes.func,
  onClose: PropTypes.func,
  options: PropTypes.object,
  safeUser: PropTypes.object,
  viewHistory: PropTypes.bool,
  pendingHistory: PropTypes.bool,
  clearRefillRequest: PropTypes.func,
  refillRequestError: PropTypes.bool,
  refillsHistoryList: PropTypes.array,
  refillRequestPending: PropTypes.bool,
  initMedicationRefill: PropTypes.func,
  setPreferredPharmacy: PropTypes.func,
  getAvailableMedications: PropTypes.func,
  refillsAvailableMedications: PropTypes.array,
  refillsAvailableMedicationsPending: PropTypes.bool,
  definePharmacy: PropTypes.oneOfType([PropTypes.object, PropTypes.number]),
};

export default enhance(Refills);



// WEBPACK FOOTER //
// ./src/components/PatientHealthboard/PatientPortal/Refills/index.jsx